import { MAX_DESCRIPTION_LENGTH, MAX_SERIALS_PER_INWARD } from "../config/returnKinds";
import type { ReturnInwardError, ReturnInwardHeader, ReturnOrderLineView, ReturnSerialRow, SubmitStatus } from "../types";

export interface ValidateReturnInwardInput {
  status: SubmitStatus;
  header: ReturnInwardHeader;
  rows: ReturnSerialRow[];
  lines: ReturnOrderLineView[];
  /** Trang chi tiết (chỉ xem) */
  readOnly: boolean;
}

/**
 * Kiểm tra trước khi lưu / hoàn thành. Trả về toàn bộ lỗi chặn để hiện một lần.
 * Các điều kiện theo kiểm tra của API create-return-in-ward, để báo ngay trên màn thay vì đợi backend từ chối.
 */
export const validateReturnInward = ({ status, header, rows, lines, readOnly }: ValidateReturnInwardInput) => {
  const errors: ReturnInwardError[] = [];

  if (readOnly) {
    errors.push({ code: "READ_ONLY", message: "Phiếu đang ở chế độ xem, không thể lưu." });
    return errors;
  }
  if (header.status === "LOCK") {
    errors.push({ code: "LOCKED", message: "Phiếu nhập kho đã được khoá." });
    return errors;
  }
  if (!header.sourceCode) {
    errors.push({ code: "NO_SOURCE", message: "Chưa có số chứng từ." });
  }
  if (!header.transactionCode) {
    errors.push({ code: "NO_TRANSACTION_CODE", message: "Chưa có mã phiếu nhập kho, vui lòng tải lại trang." });
  }
  if (!header.subInvId) {
    errors.push({
      code: "NO_WAREHOUSE",
      message: "Chưa chọn kho nhập",
      detail: "Đổi trả chỉ nhập vào kho hàng bán, kho xử lý hoặc kho bảo hành của chi nhánh.",
    });
  }
  if (rows.length === 0) {
    errors.push({ code: "NO_ROWS", message: "Chưa có serial nào để nhập." });
  }
  if (rows.length > MAX_SERIALS_PER_INWARD) {
    errors.push({
      code: "TOO_MANY_ROWS",
      message: `Số lượng serial vượt quá ${MAX_SERIALS_PER_INWARD}, vui lòng kiểm tra lại.`,
    });
  }

  rows.forEach((row, index) => {
    const serial = (row.serial ?? "").trim();
    const where = `Dòng ${index + 1} · ${serial}${row.itemName ? ` · ${row.itemName}` : ""}`;
    if (row.warning) {
      errors.push({ code: "INVALID_ROW", message: "Serial không hợp lệ", detail: `${where} · ${row.warning}`, serial });
    }
    if (!(Number(row.locatorId) > 0)) {
      errors.push({ code: "NO_POSITION", message: "Serial chưa chọn vị trí nhập", detail: where, serial });
    }
    if (!(Number(row.primaryQuantity) > 0)) {
      errors.push({ code: "INVALID_QUANTITY", message: "Số lượng nhập không hợp lệ", detail: where, serial });
    }
  });

  if ((header.description?.length ?? 0) > MAX_DESCRIPTION_LENGTH) {
    errors.push({
      code: "DESCRIPTION_TOO_LONG",
      message: `Ghi chú phiếu nhập không quá ${MAX_DESCRIPTION_LENGTH} ký tự.`,
    });
  }

  // Hoàn thành: backend yêu cầu tổng số nhập bằng tổng số trên đơn đổi trả, nên báo từng dòng lệch
  if (status === "LOCK") {
    lines.forEach((line) => {
      const required = Number(line.primaryQuantity) || 0;
      const variance = line.scannedQuantity - required;
      if (variance === 0) return;
      errors.push({
        code: "VARIANCE",
        message: "Số lượng nhập chưa khớp đơn đổi trả",
        detail: `${line.itemName || line.itemCode} · đã quét ${line.scannedQuantity} / ${required}, ${
          variance < 0 ? `thiếu ${-variance}` : `thừa ${variance}`
        }`,
        orderSerial: (line.serial ?? "").trim() || undefined,
      });
    });
  }

  return errors;
};
